// Story 11.1 — Panel derecho con la lista de errores/warnings de la última
// validación. Click en un problema → callback al editor para revealLine.

import { AlertTriangle, ChevronDown, ChevronRight, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import type { ValidationProblem, ValidationReport } from '@/lib/editor-types';
import { cn } from '@/lib/utils';

interface Props {
  report: ValidationReport | null;
  onJump?: (path: string, line: number) => void;
  onClose?: () => void;
}

function severityClass(kind: 'error' | 'warning'): string {
  return kind === 'error'
    ? 'border-l-destructive bg-destructive/5 text-destructive'
    : 'border-l-warning bg-warning/5 text-warning';
}

export function ProblemsPanel({ report, onJump, onClose }: Props): React.ReactElement {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(group: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }

  const errors = report?.errors ?? [];
  const warnings = report?.warnings ?? [];
  const total = errors.length + warnings.length;
  const grouped = groupByFile([...errors, ...warnings]);

  return (
    <aside
      className="flex h-full flex-col border-l border-border bg-card text-sm"
      aria-label="Panel de problemas"
    >
      <header className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />
          Problemas
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{total}</span>
        </div>
        {onClose && (
          <Button size="icon" variant="ghost" aria-label="Cerrar panel" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </header>
      <div className="flex-1 overflow-y-auto">
        {total === 0 ? (
          <p className="p-3 text-muted-foreground">
            Sin problemas detectados en la última validación.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {Array.from(grouped.entries()).map(([file, problems]) => {
              const open = expanded.has(file) || expanded.size === 0;
              return (
                <li key={file}>
                  <button
                    type="button"
                    onClick={() => toggle(file)}
                    className="flex w-full items-center gap-1 px-3 py-1.5 text-left text-xs uppercase tracking-wide text-muted-foreground"
                  >
                    {open ? (
                      <ChevronDown className="h-3 w-3" aria-hidden />
                    ) : (
                      <ChevronRight className="h-3 w-3" aria-hidden />
                    )}
                    <span className="truncate">{file}</span>
                    <span className="ml-auto text-[10px]">{problems.length}</span>
                  </button>
                  {open && (
                    <ul>
                      {problems.map((p) => (
                        <li
                          key={`${p.code}::${p.location?.line ?? 0}::${p.message.slice(0, 32)}`}
                          className={cn(
                            'border-l-2 px-3 py-1.5',
                            severityClass(errors.includes(p) ? 'error' : 'warning'),
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => onJump?.(file, p.location?.line ?? 1)}
                            className="text-left"
                          >
                            <div className="font-mono text-xs">{p.code}</div>
                            <div className="text-foreground">{p.message}</div>
                            {p.suggestion && (
                              <div className="mt-0.5 text-[10px] italic text-muted-foreground">
                                💡 {p.suggestion}
                              </div>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}

function groupByFile(problems: ValidationProblem[]): Map<string, ValidationProblem[]> {
  const map = new Map<string, ValidationProblem[]>();
  for (const p of problems) {
    const file = p.location?.filePath ?? 'config';
    const arr = map.get(file) ?? [];
    arr.push(p);
    map.set(file, arr);
  }
  return map;
}
